/**
 * MongoDB Connector Types
 *
 * Shared types for MongoDB connector components used across frontend and self-managed portals.
 */

import { MongoConnectionSecret, MongoConnectionString } from './operator'

/**
 * Extract the raw connection string value from MongoConnectionString or plain string.
 * - Self-managed (Operator API): Returns value from { direct } or { awsSecretsManager }
 * - Cloud (GraphQL): Returns the plain string as-is
 * - If connectionSecret is provided, returns a display string for the secret reference
 */
export function getConnectionStringValue(
  cs: MongoConnectionString | string | undefined,
  connectionSecret?: MongoConnectionSecret,
): string {
  if (connectionSecret) {
    return `Kubernetes Secret: ${connectionSecret.kubernetesSecret.name}`
  }
  if (!cs) {
    return 'Connection configured via secret'
  }
  if (typeof cs === 'string') {
    return cs
  }
  if ('direct' in cs) {
    return cs.direct
  }
  return cs.awsSecretsManager
}

/**
 * Represents a MongoDB collection configuration for syncing with Ditto
 */
export interface MongoCollection {
  /** The name of the MongoDB collection */
  name: string
  /** MongoDB fields to map to Ditto document IDs */
  idMappingFields: string[]
  /** Whether to sync existing documents from MongoDB to Ditto on setup */
  initialSync: boolean
  /** Enable strict DQL mode (disables BSON type support) */
  dqlStrictMode?: boolean
  /** Storage mode: NATIVE (default) or EJSON for preserving BSON types */
  mode?: 'NATIVE' | 'EJSON'
  /** Ditto-specific type mappings */
  dittoTypes?: {
    /** Fields to treat as settable counters */
    settableCounters?: string[]
  }
}

/**
 * Represents a configured MongoDB connector link
 */
export interface MongoConnectorLink {
  /** Unique identifier for the connector */
  id: string
  /**
   * MongoDB connection string configuration
   * - Self-managed (Operator API): MongoConnectionString object with direct or awsSecretsManager
   * - Cloud (GraphQL): Plain string
   */
  connectionString?: MongoConnectionString | string
  /** Kubernetes secret containing the connection string (self-managed only) */
  connectionSecret?: MongoConnectionSecret
  /** Target MongoDB database name */
  database: string
  /** Collections configured for syncing */
  collections: MongoCollection[]
}

/**
 * Possible states for the MongoDB connector
 */
export type ConnectorState = 'pending' | 'running' | 'error' | 'exiting'

/**
 * Real-time status of the MongoDB connector
 */
export interface ConnectorStatus {
  /** Current state of the connector */
  state: ConnectorState
  /** Error messages if state is 'error' */
  errorMessages: string[]
  /** When the status was last updated */
  updatedAt: Date
}

/**
 * Possible states for initial sync progress
 */
export type InitialSyncState =
  | 'completed'
  | 'in_progress'
  | 'partially_completed'
  | 'failed'

/**
 * Initial sync progress for a single collection
 */
export interface ConnectorInitialSyncStatus {
  /** Name of the collection being synced */
  collectionName: string
  /** Current sync status */
  status: InitialSyncState
  /** Number of documents successfully synced */
  syncedDocumentCount: number
  /** Number of documents that failed to sync (if any) */
  failedDocumentCount?: number
  /** Total estimated documents to sync */
  totalEstimatedDocumentCount: number
}

/**
 * Feature flags that affect MongoDB connector behavior
 */
export interface MongoConnectorFeatureFlags {
  /** Enable DQL strict mode option in collection config */
  mongoDqlStrictMode?: boolean
  /** Enable settable counters field type option */
  enableSettableCounters?: boolean
}
